import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { apiFetch, ApiError } from './api';
import { DealPicker, type Deal } from './DealPicker';
import { BILLING_LABELS, type BillingType } from './lib/billingType';

export type EditTimeEntryDeal = Deal;

export interface EditTimeEntryNote {
  id: string;
  note: string;
  createdAt: string;
}

export interface EditTimeEntryEntry {
  id: string;
  dealId: string;
  projectId: string | null;
  startedAt: string;
  minutes: number;
  note: string | null;
  notes: EditTimeEntryNote[];
  billingType: BillingType;
}

interface EditTimeEntryProps {
  entry: EditTimeEntryEntry;
  employeeId: string;
  deals: EditTimeEntryDeal[];
  onClose: () => void;
  // Billing types the caller has configured as flat. Used to hide the
  // Minutes input (and its time-equivalent leak) on flat entries.
  flatBillingTypes: string[];
  isSupervisor: boolean;
}

interface PatchBody {
  dealId?: string;
  projectId?: string | null;
  startedAt?: string;
  minutes?: number;
  note?: string | null;
  billingType?: BillingType;
}

const NOTE_TIME_FMT = new Intl.DateTimeFormat('en-CA', {
  hour: 'numeric',
  minute: '2-digit',
});

// Convert ISO string → value usable by <input type="datetime-local"> in local time.
function isoToLocalInput(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number): string => String(n).padStart(2, '0');
  const yyyy = d.getFullYear();
  const mm = pad(d.getMonth() + 1);
  const dd = pad(d.getDate());
  const hh = pad(d.getHours());
  const mi = pad(d.getMinutes());
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
}

interface NoteRowProps {
  note: EditTimeEntryNote;
  entryId: string;
  employeeId: string;
}

function NoteRow({ note, entryId, employeeId }: NoteRowProps): JSX.Element {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<boolean>(false);
  const [draft, setDraft] = useState<string>(note.note);

  const invalidate = (): void => {
    qc.invalidateQueries({ queryKey: ['recent', employeeId] });
  };

  const patchMutation = useMutation({
    mutationFn: (text: string) =>
      apiFetch<EditTimeEntryNote>(`/time-entries/${entryId}/notes/${note.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ text }),
      }),
    onSuccess: () => {
      invalidate();
      setEditing(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () =>
      apiFetch<{ ok: true }>(`/time-entries/${entryId}/notes/${note.id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      invalidate();
    },
  });

  const onSave = (): void => {
    const text = draft.trim();
    if (!text || text === note.note) {
      setEditing(false);
      setDraft(note.note);
      return;
    }
    patchMutation.mutate(text);
  };

  const onDelete = (): void => {
    if (!window.confirm('Delete this note?')) return;
    deleteMutation.mutate();
  };

  if (editing) {
    return (
      <li className="flex items-start gap-2 text-xs">
        <span className="font-mono tabular-nums text-brand-slate shrink-0 pt-2">
          {NOTE_TIME_FMT.format(new Date(note.createdAt))}
        </span>
        <input
          type="text"
          value={draft}
          autoFocus
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              onSave();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              setEditing(false);
              setDraft(note.note);
            }
          }}
          className="flex-1 rounded-md bg-brand-white px-2 py-1 text-xs text-brand-charcoal outline-none ring-1 ring-brand-steel focus:ring-2 focus:ring-brand-blue"
        />
        <button
          type="button"
          onClick={onSave}
          disabled={patchMutation.isPending}
          className="rounded-md bg-brand-blue px-2 py-1 text-[11px] font-semibold text-brand-white hover:bg-[#0067BB] disabled:opacity-50"
        >
          {patchMutation.isPending ? '…' : 'Save'}
        </button>
        <button
          type="button"
          onClick={() => {
            setEditing(false);
            setDraft(note.note);
          }}
          className="rounded-md bg-brand-white px-2 py-1 text-[11px] text-brand-slate ring-1 ring-brand-steel hover:bg-brand-steel"
        >
          Cancel
        </button>
      </li>
    );
  }

  return (
    <li className="flex items-start gap-2 text-xs">
      <span className="font-mono tabular-nums text-brand-slate shrink-0 pt-0.5">
        {NOTE_TIME_FMT.format(new Date(note.createdAt))}
      </span>
      <span className="flex-1 text-brand-charcoal">{note.note}</span>
      <button
        type="button"
        onClick={() => setEditing(true)}
        className="rounded-md bg-brand-charcoal/5 px-2 py-0.5 text-[11px] text-brand-slate hover:bg-brand-charcoal/10"
      >
        Edit
      </button>
      <button
        type="button"
        onClick={onDelete}
        disabled={deleteMutation.isPending}
        className="rounded-md bg-brand-charcoal/5 px-2 py-0.5 text-[11px] text-red-600 hover:bg-red-50 disabled:opacity-50"
      >
        {deleteMutation.isPending ? '…' : 'Delete'}
      </button>
    </li>
  );
}

interface AddNoteProps {
  entryId: string;
  employeeId: string;
}

function AddNote({ entryId, employeeId }: AddNoteProps): JSX.Element {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<string>('');

  const mutation = useMutation({
    mutationFn: (text: string) =>
      apiFetch<EditTimeEntryNote>(`/time-entries/${entryId}/notes`, {
        method: 'POST',
        body: JSON.stringify({ text }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['recent', employeeId] });
      setDraft('');
    },
  });

  const onAdd = (): void => {
    const text = draft.trim();
    if (!text) return;
    mutation.mutate(text);
  };

  return (
    <div className="flex gap-2">
      <input
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            onAdd();
          }
        }}
        placeholder="Add a note…"
        className="flex-1 rounded-md bg-brand-white px-3 py-1.5 text-xs text-brand-charcoal outline-none ring-1 ring-brand-steel placeholder:text-brand-slate/60 focus:ring-2 focus:ring-brand-blue"
      />
      <button
        type="button"
        onClick={onAdd}
        disabled={!draft.trim() || mutation.isPending}
        className="rounded-md bg-brand-blue px-3 py-1.5 text-xs font-semibold text-brand-white hover:bg-[#0067BB] disabled:opacity-40"
      >
        {mutation.isPending ? 'Adding…' : 'Add'}
      </button>
    </div>
  );
}

export function EditTimeEntry({ entry, employeeId, deals, onClose, flatBillingTypes, isSupervisor }: EditTimeEntryProps): JSX.Element {
  const flatSet = new Set(flatBillingTypes);
  const qc = useQueryClient();
  const [dealId, setDealId] = useState<string>(entry.dealId);
  const [minutes, setMinutes] = useState<number>(entry.minutes);
  const [startedAtLocal, setStartedAtLocal] = useState<string>(isoToLocalInput(entry.startedAt));
  const [note, setNote] = useState<string>(entry.note ?? '');
  const [billingType, setBillingType] = useState<BillingType>(entry.billingType);

  const mutation = useMutation({
    mutationFn: (body: PatchBody) =>
      apiFetch<EditTimeEntryEntry>(`/time-entries/${entry.id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['recent', employeeId] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      qc.invalidateQueries({ queryKey: ['admin', 'billing-summary'] });
      onClose();
    },
  });

  // Tim's trial feedback: accidental double-entries need a self-serve
  // delete, not just edit. Server allows the owning employee or a
  // supervisor; locked entries 423 regardless.
  const deleteEntry = useMutation({
    mutationFn: () =>
      apiFetch<{ ok: true }>(`/time-entries/${entry.id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['recent', employeeId] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      qc.invalidateQueries({ queryKey: ['admin', 'billing-summary'] });
      onClose();
    },
  });

  const onDeleteEntry = (): void => {
    if (!window.confirm('Delete this entry? This can’t be undone.')) return;
    deleteEntry.mutate();
  };

  const onSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    const body: PatchBody = {};
    if (dealId !== entry.dealId) body.dealId = dealId;
    if (minutes !== entry.minutes) body.minutes = minutes;
    const newIso = new Date(startedAtLocal).toISOString();
    if (newIso !== new Date(entry.startedAt).toISOString()) body.startedAt = newIso;
    const trimmedNote = note.trim();
    const originalNote = entry.note ?? '';
    if (trimmedNote !== originalNote) body.note = trimmedNote === '' ? null : trimmedNote;
    if (billingType !== entry.billingType) body.billingType = billingType;
    if (Object.keys(body).length === 0) {
      onClose();
      return;
    }
    mutation.mutate(body);
  };

  return (
    <div className="mt-2 flex flex-col gap-3 rounded-lg bg-brand-white p-3 ring-1 ring-brand-steel">
      <form onSubmit={onSubmit} className="flex flex-col gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-xs uppercase tracking-wide font-semibold text-brand-charcoal">Job</span>
          <DealPicker deals={deals} value={dealId} onChange={setDealId} />
        </label>
        {/* Billing-type radio removed (Tim's trial feedback: hourly-only).
            billingType stays whatever the entry already is — legacy flat
            entries keep their type; the flat-aware minutes handling below
            still applies to them. */}
        {(() => {
          const isFlat = flatSet.has(billingType);
          if (isFlat && !isSupervisor) {
            // Employee view of a flat entry: minutes is a derived value
            // they shouldn't see. Show only the unit and the When picker.
            return (
              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col gap-1">
                  <span className="text-xs uppercase tracking-wide font-semibold text-brand-charcoal">Unit</span>
                  <div className="rounded-lg bg-brand-mist px-3 py-2 text-base text-brand-charcoal ring-1 ring-brand-steel">
                    1 {BILLING_LABELS[billingType]}
                  </div>
                </div>
                <label className="flex flex-col gap-1">
                  <span className="text-xs uppercase tracking-wide font-semibold text-brand-charcoal">When</span>
                  <input
                    type="datetime-local"
                    value={startedAtLocal}
                    onChange={(e) => setStartedAtLocal(e.target.value)}
                    className="rounded-lg bg-brand-white px-3 py-2 text-base text-brand-charcoal outline-none ring-1 ring-brand-steel focus:ring-2 focus:ring-brand-blue"
                  />
                </label>
              </div>
            );
          }
          return (
            <div className="grid grid-cols-2 gap-3">
              <label className="flex flex-col gap-1">
                <span className="text-xs uppercase tracking-wide font-semibold text-brand-charcoal">
                  {isFlat ? 'Minutes (equiv)' : 'Minutes'}
                </span>
                <input
                  type="number"
                  min={1}
                  max={24 * 60}
                  value={minutes}
                  onChange={(e) => setMinutes(Number(e.target.value))}
                  className="rounded-lg bg-brand-white px-3 py-2 text-base text-brand-charcoal outline-none ring-1 ring-brand-steel focus:ring-2 focus:ring-brand-blue"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs uppercase tracking-wide font-semibold text-brand-charcoal">When</span>
                <input
                  type="datetime-local"
                  value={startedAtLocal}
                  onChange={(e) => setStartedAtLocal(e.target.value)}
                  className="rounded-lg bg-brand-white px-3 py-2 text-base text-brand-charcoal outline-none ring-1 ring-brand-steel focus:ring-2 focus:ring-brand-blue"
                />
              </label>
            </div>
          );
        })()}
        <label className="flex flex-col gap-1">
          <span className="text-xs text-brand-slate">Note (legacy)</span>
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="rounded-lg bg-brand-white px-3 py-2 text-base text-brand-charcoal outline-none ring-1 ring-brand-steel focus:ring-2 focus:ring-brand-blue"
          />
        </label>
        <div className="flex gap-2">
          <button
            type="submit"
            disabled={mutation.isPending}
            className="min-h-[40px] flex-1 rounded-lg bg-brand-blue px-4 text-sm font-semibold text-brand-white hover:bg-[#0067BB] active:scale-95 transition-transform disabled:opacity-50"
          >
            {mutation.isPending ? 'Saving…' : 'Save'}
          </button>
          <button
            type="button"
            onClick={onClose}
            disabled={mutation.isPending}
            className="min-h-[40px] flex-1 rounded-lg bg-brand-white px-4 text-sm font-semibold text-brand-slate ring-1 ring-brand-steel hover:bg-brand-steel active:scale-95 transition-transform disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onDeleteEntry}
            disabled={mutation.isPending || deleteEntry.isPending}
            className="min-h-[40px] rounded-lg bg-red-50 px-4 text-sm font-semibold text-red-700 ring-1 ring-red-200 hover:bg-red-100 active:scale-95 transition-transform disabled:opacity-50"
          >
            {deleteEntry.isPending ? 'Deleting…' : 'Delete'}
          </button>
        </div>
        {deleteEntry.isError && (
          deleteEntry.error instanceof ApiError && deleteEntry.error.status === 423 ? (
            <p className="rounded-md bg-amber-50 p-2 text-sm text-amber-800 ring-1 ring-amber-200">
              This entry is part of an approved pay period and can&rsquo;t be deleted.
            </p>
          ) : (
            <p className="text-sm text-red-600">Delete failed: {String(deleteEntry.error)}</p>
          )
        )}
        {mutation.isError && (
          mutation.error instanceof ApiError && mutation.error.status === 423 ? (
            <p className="rounded-md bg-amber-50 p-2 text-sm text-amber-800 ring-1 ring-amber-200">
              This entry is part of an approved pay period. Ask Tim to unlock the period before editing.
            </p>
          ) : (
            <p className="text-sm text-red-600">Failed: {String(mutation.error)}</p>
          )
        )}
      </form>

      <div className="flex flex-col gap-2 border-t border-brand-steel pt-3">
        <span className="text-xs uppercase tracking-wide font-semibold text-brand-charcoal">Note thread</span>
        {entry.notes.length === 0 ? (
          <p className="text-xs text-brand-slate">No notes yet.</p>
        ) : (
          <ul className="flex flex-col gap-1.5">
            {entry.notes.map((n) => (
              <NoteRow key={n.id} note={n} entryId={entry.id} employeeId={employeeId} />
            ))}
          </ul>
        )}
        <AddNote entryId={entry.id} employeeId={employeeId} />
      </div>
    </div>
  );
}
